import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    TextInput,
    Flex,
    Box,
    Title,
    Text,
    Tooltip,
    Card,
    Divider,
    Loader,
    Group,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { IconDeviceFloppy, IconInfoCircle, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import NextLink from 'next/link';

import * as Enums from "@/utils/enums";
import Fetch from "@/utils/Fetch";
import TemplateService from "@/services/template-service";
import Helper from "@/utils/helper";
import ConfirmAction from "@/components/modals/confirm-action";
import ScRichTextEditorV2, {
    DEFAULT_EMAIL_MIN_HEIGHT,
    DEFAULT_SMS_MIN_HEIGHT
} from "@/PageComponents/Message/Communication/ScRichTextEditorV2";
import { ReplacementTagInfo, ReplacementTag } from "@/PageComponents/Message/Communication/ReplacementTagInfo";

import classes from "./EditTemplateV2.module.css";

const MAX_SMS_CHAR = 425;
const MAX_CONTENT_SIZE = 1048576 / 2; // 500 KB in bytes

const templateToFormValues = (template: any) => ({
    ID: template?.ID,
    Name: template?.Name || '',
    IsActive: template?.IsActive || false,
    Subject: template?.Subject || '',
    EmailBody: template?.EmailBody || '',
    SMSBody: template?.SMSBody || '',
    Module: +template?.Module,
    TemplateType: +template?.TemplateType,
});

interface EditTemplateV2Props {
    template: any;
    fromCreate?: boolean;
    fromExternalModule?: boolean;
    onSave?: (id: string) => void;
    onSaved?: (savedTemplate: any) => void;
    onCancel?: () => void;
}

const submitData = async (templateToSave: any, usePostMethod: boolean) => {
    let templateResponse: any = {};
    if (usePostMethod) {
        templateResponse = await Fetch.post({
            url: '/Template',
            params: templateToSave
        });
    } else {
        templateResponse = await Fetch.put({
            url: '/Template',
            params: templateToSave
        });
    }

    if (templateResponse.ID) {
        return templateResponse;
    } else {
        throw new Error(templateResponse.serverMessage || templateResponse.message || 'something went wrong');
    }
}

const EditTemplateV2: FC<EditTemplateV2Props> = ({ ...props }) => {
    const router = useRouter();
    const [confirmOptions, setConfirmOptions] = useState(Helper.initialiseConfirmOptions());
    const nextUrlRef = useRef<string | null>(null);

    const form = useForm({
        initialValues: templateToFormValues(props.template),
        validateInputOnBlur: true,
        validateInputOnChange: true,
        validate: {
            Name: (value) => (value.trim().length === 0 ? 'Template name is required' : null),
            Subject: (val) => {
                const invalidTags = TemplateService.testForValidSubject(val);
                if (invalidTags.length !== 0) {
                    return 'Unfortunately ' + invalidTags.join(', ') + ' is not allowed in the message subject';
                }
                if (val && val.length > 255) {
                    return 'Subject must be 255 characters or less';
                }
                if (!val || val.trim().length === 0) {
                    return 'Subject is required when sending emails';
                }
                return null;
            },
            SMSBody: (val) => {
                if (!val || val.trim().length === 0) {
                    return 'Please enter an SMS message';
                }
                if (val?.length > MAX_SMS_CHAR) {
                    return `SMS content exceeds the maximum allowed characters of ${MAX_SMS_CHAR}`;
                }
                return null;
            },
            EmailBody: (val) => {
                const hasImage = /<img\b/i.test(val || '');
                const effectivelyEmpty = !val || (val.replace(/<[^>]*>/g, '').trim().length === 0 && !val.includes('{{') && !hasImage);
                if (effectivelyEmpty) {
                    return 'Email content is required';
                }
                const size = new Blob([val]).size;
                if (size > MAX_CONTENT_SIZE) {
                    const excessSize = size - MAX_CONTENT_SIZE;
                    let excessSizeMessage = '';
                    if (excessSize < 1024) {
                        excessSizeMessage = `${excessSize} bytes`;
                    } else if (excessSize < 1048576) {
                        excessSizeMessage = `${(excessSize / 1024).toFixed(2)} KB`;
                    } else {
                        excessSizeMessage = `${(excessSize / 1048576).toFixed(2)} MB`;
                    }
                    return `Total content exceeds the 500KB limit by ${excessSizeMessage}`;
                }
                return null;
            },
        },
    });

    // Mantine's useForm captures initialValues once at mount. When the parent swaps
    // the template — navigating to another record, or the post-save version bump that
    // replaces the route id — re-sync the form to the incoming record so it doesn't
    // keep showing the previously-loaded values.
    useEffect(() => {
        const values = templateToFormValues(props.template);
        form.setValues(values);
        form.resetDirty(values);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.template?.ID]);

    // Save mutation
    const { mutate: saveMutate, isLoading: isSaving } = useMutation(
        (templateToSave: any) => submitData(templateToSave, !!props.fromCreate),
        {
            onSuccess: async (data: any) => {
                showNotification({
                    message: 'Template saved successfully!',
                    color: 'scBlue',
                    autoClose: 1200
                });

                Helper.mixpanelTrack(props.fromCreate ? 'create-template' : 'edit-template', { templateID: data.ID } as any);

                const values = templateToFormValues(data);
                form.setValues(values);
                form.resetDirty(values);

                if (props.onSave) {
                    props.onSave(data.ID);
                }

                if (nextUrlRef.current) {
                    const destination = nextUrlRef.current;
                    nextUrlRef.current = null;
                    await Helper.waitABit(200);
                    Helper.nextRouter(router.push, destination);
                } else if (props.fromCreate && !props.fromExternalModule) {
                    await Helper.waitABit();
                    Helper.nextRouter(router.push, `/settings/template/${data.ID}`);
                } else {
                    // Template saves are versioned — each successful save returns a NEW ID.
                    // Hand the saved record to the parent so a route-owning page can sync its
                    // URL/query to the latest version. Modal/external-module consumers that
                    // don't pass onSaved are unaffected.
                    await Helper.waitABit();
                    props.onSaved?.(data);
                }
            },
            onError: (error: Error) => {
                nextUrlRef.current = null;
                showNotification({
                    message: 'An error occurred while saving the template: ' + error.message,
                    color: 'yellow.7',
                    autoClose: 4000
                })
            }
        }
    );

    // Delete mutation
    const { mutate: deleteMutate, isLoading: isDeleting } = useMutation(
        (templateToSave: any) => submitData(templateToSave, false),
        {
            onSuccess: async (data: any) => {
                if (data.ID) {
                    showNotification({
                        title: 'Success',
                        message: 'Template deleted successfully!',
                        color: 'green',
                    });

                    form.reset();
                    await Helper.waitABit();
                    router.replace('/settings/template/list');
                } else {
                    showNotification({
                        title: 'Error',
                        message: data.serverMessage,
                        color: 'yellow.7',
                    });
                }
            },
            onError: () => {
                showNotification({
                    title: 'Error',
                    message: 'An error occurred while deleting the template.',
                    color: 'yellow.7',
                });
            }
        }
    );

    const saveTemplate = (values: any) => {
        if (form.isValid()) {
            saveMutate({
                ...props.template,
                ...values
            });
        }
    }

    const handleErrors = (errors: any) => {
        showNotification({
            message: 'There are errors on the page',
            color: 'yellow',
        });
    };

    const deleteTemplate = () => {
        setConfirmOptions({
            ...Helper.initialiseConfirmOptions(),
            confirmButtonText: 'Delete Template',
            heading: 'Confirm delete template?',
            text: 'This action will be permanent',
            display: true,
            onConfirm: async () => {
                deleteMutate({ ...props.template, IsActive: false })
            },
        });
    };


    const { data: replacementTags = [] } = useQuery(['replacementTags'], async () => {
        const { Results: tags, ...res } = await Fetch.get({
            url: `/Template/GetReplacementTagList?module=${props.template?.Module}&searchPhrase=${''}`
        })
        if (tags) {
            return tags as ReplacementTag[]
        } else {
            throw new Error(res.serverMessage || res.message || 'something went wrong')
        }
    });

    const subjectReplacementTags = useMemo(() => {
        return replacementTags.filter(tag => {
            const invalidTags = TemplateService.testForValidSubject(`{{${tag.Name}}}`);
            return invalidTags.length === 0;
        });
    }, [replacementTags]);

    !props.fromExternalModule && Helper.preventRouteChange(
        form.isDirty(),
        () => form.resetDirty(),
        setConfirmOptions,
        (nextUrl: any) => {
            nextUrlRef.current = nextUrl;
            saveTemplate(form.values);
        },
        true
    );

    const headerSection = (
        <Flex
            w={'100%'}
            align={'center'}
            px={'lg'}
            py={'xs'}
            bg={'gray.0'}
            pos={'sticky'}
            top={0}
            style={{ borderBottom: '1px solid var(--mantine-color-gray-3)', zIndex: props.fromExternalModule ? 1010 : 100 }}
        >
            <Title order={3} fw={700} c={'dark.7'}>Edit Template</Title>
            <Group ml={'auto'} gap={'sm'}>
                {!props.fromExternalModule && !props.fromCreate && (
                    <Tooltip 
                        color={'scBlue'}
                        disabled={props.template?.TemplateType === Enums.TemplateType.User}
                        label={"Cannot delete system template"}
                    >
                        <Button
                            variant="outline"
                            color="red.7"
                            loading={isDeleting}
                            onClick={deleteTemplate}
                            disabled={props.template?.TemplateType !== Enums.TemplateType.User || !form.values.ID || isSaving}
                            leftSection={<IconTrash size={16} />}
                        >
                            Delete
                        </Button>
                    </Tooltip>
                )}

                {props.onCancel ? (
                    <Button variant={'default'} disabled={isSaving} onClick={props.onCancel}>
                        Cancel
                    </Button>
                ) : (
                    <NextLink href={'/settings/template/list'}>
                        <Button variant={'default'} disabled={isSaving}>
                            Cancel
                        </Button>
                    </NextLink>
                )}

                <Button
                    leftSection={isSaving ? <Loader size={18} color="white" /> : <IconDeviceFloppy size={16} />}
                    onClick={() => form.onSubmit(saveTemplate, handleErrors)()}
                    loading={isSaving}
                >
                    Save
                </Button>
            </Group>
        </Flex>
    );

    return (
        <Box className={classes.root}>
            <ConfirmAction options={confirmOptions} setOptions={setConfirmOptions} />
            <form onSubmit={form.onSubmit(saveTemplate, handleErrors)}>
                {headerSection}

                <Box p="lg">
                    <Flex align={'center'} gap={'sm'} mb='lg' >
                        <Text c={'dimmed'} size={'md'}>
                            Module: {Enums.getEnumStringValue(Enums.Module, props.template?.Module, true)}
                        </Text>
                    </Flex>

                    <Flex gap={'lg'} direction={{ base: 'column', md: 'row' }} wrap={'wrap'} mb="xl">
                        <Box flex={1}>
                            <TextInput
                                label="Template Name"
                                placeholder="Enter template name"
                                withAsterisk
                                {...form.getInputProps('Name')}
                            />
                        </Box>
                        <Box flex={1} maw={500} ml={{ md: 'auto' }}>
                            <ReplacementTagInfo replacementTags={replacementTags} />
                        </Box>
                    </Flex>

                    <Flex
                        gap={'lg'}
                        wrap={'wrap'}
                        align={'stretch'}
                    >
                        {/* Email Card */}
                        <Box style={{ flexGrow: 1, flexBasis: 360, display: 'flex', flexDirection: 'column' }}>
                            <Card
                                withBorder
                                radius={'md'}
                                p={0}
                                style={{
                                    flexGrow: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderColor: form.errors.EmailBody ? 'var(--mantine-color-yellow-6)' : undefined,
                                }}
                            >
                                <Box p="lg" pb="xs">
                                    <Text fw={600} c={'dark.7'}>Email</Text>
                                </Box>

                                <Box px="lg">
                                    <ScRichTextEditorV2
                                        mode="subject"
                                        placeholder="Subject"
                                        replacementTags={subjectReplacementTags}
                                        allReplacementTags={replacementTags}
                                        {...form.getInputProps('Subject')}
                                        bg="gray.1"
                                        unstyled
                                    />
                                </Box>

                                <Box px="lg" style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minHeight: DEFAULT_EMAIL_MIN_HEIGHT
                                }}>
                                    <ScRichTextEditorV2
                                        mode="email"
                                        {...form.getInputProps('EmailBody')}
                                        replacementTags={replacementTags}
                                        hideError={true}
                                        showHtmlEditButton
                                        flex={1}
                                        maxHeight={props.fromExternalModule ? 450 : undefined}
                                    />
                                </Box>
                            </Card>
                            {form.errors.EmailBody && (
                                <Text mt={5} size="11px" c="yellow.6">
                                    {form.errors.EmailBody as string}
                                </Text>
                            )}
                        </Box>

                        {/* SMS Card */}
                        <Box style={{ flexGrow: 1, flexBasis: 360, display: 'flex', flexDirection: 'column', maxWidth: 500 }}>
                            <Card
                                withBorder
                                radius={'md'}
                                p={0}
                                style={{
                                    flexGrow: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderColor: form.errors.SMSBody ? 'var(--mantine-color-yellow-6)' : undefined,
                                }}
                            >
                                <Flex justify="start" align="center" p="lg" pb="xs">
                                    <Text fw={600} c={'dark.7'}>SMS</Text>
                                    <Tooltip
                                        maw={380}
                                        multiline
                                        label="SMS messages consume 1-3 credits depending on length. Note: Replacement tags can make messages longer, potentially causing truncation."
                                        withArrow position="top" color={'scBlue'}>
                                        <Flex justify={'center'} miw={40} style={{ cursor: 'help' }}>
                                            <IconInfoCircle size="1.2rem" color="var(--mantine-color-scBlue-6)" />
                                        </Flex>
                                    </Tooltip>
                                    {/*<Flex align="center" gap={'xs'} ml={'auto'}>
                                        <Text size={'sm'} fw={500} style={{ color: form.values.SMSBody?.length > MAX_SMS_CHAR ? 'var(--mantine-color-yellow-7)' : 'inherit' }}>
                                            {form.values.SMSBody?.length || 0} / {MAX_SMS_CHAR}
                                        </Text>
                                    </Flex>*/}
                                </Flex>

                                <Divider mx={'lg'} />

                                <Box px="lg" style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    containerType: 'size',
                                    minHeight: DEFAULT_SMS_MIN_HEIGHT
                                }}>
                                    <ScRichTextEditorV2
                                        mode="sms"
                                        placeholder="Type your SMS here.."
                                        replacementTags={replacementTags}
                                        {...form.getInputProps('SMSBody')}
                                        hideError={true}
                                        flex={1}
                                        maxHeight="100cqh"
                                    />
                                </Box>
                            </Card>
                            {form.errors.SMSBody && (
                                <Text mt={5} size="11px" c="yellow.6">
                                    {form.errors.SMSBody as string}
                                </Text>
                            )}
                        </Box>
                    </Flex>
                </Box>
            </form>
        </Box>
    );
}

export default EditTemplateV2;
